import { useState, useEffect } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@/components/tailwind/button'
import { Heading } from '@/components/tailwind/heading'
import { DataTable } from '@/components/data-table'
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '@/components/tailwind/dialog'
import { createBillingRunColumns, type BillingRunWithProgress } from './billing-runs-columns'
import { useAuth } from '@/context/use-auth'
import { getBillingRuns, getBillingRunItems, createBillingRun, deleteBillingRun, getBillingRunDeleteCheck, type BillingRunDeleteCheck } from '@/services/billing-services'
import { useBillingTranslation } from '@/i18n/hooks'
import { calculateBillingProgress } from '@/utils/billing-progress'
import log from '@/lib/logger'

function BillingOverview() {
  const [billingRunsWithProgress, setBillingRunsWithProgress] = useState<BillingRunWithProgress[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [creating, setCreating] = useState(false)
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null)
  const [deleteCheck, setDeleteCheck] = useState<BillingRunDeleteCheck | null>(null)
  const [deleting, setDeleting] = useState(false)
  const { token } = useAuth()
  const { t } = useBillingTranslation()

  useEffect(() => {
    fetchBillingRuns()
  }, [])

  async function fetchBillingRuns() {
    if (!token) {
      log.error('No token available for fetching billing runs')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const data = await getBillingRuns(token)

      const runsWithProgress: BillingRunWithProgress[] = []
      for (const run of data) {
        try {
          const items = await getBillingRunItems(token, run.id)
          const progress = calculateBillingProgress(items)

          runsWithProgress.push({
            ...run,
            progress
          })
        } catch (error) {
          log.error(`Error fetching items for billing run ${run.id}:`, error)
          runsWithProgress.push({
            ...run,
            progress: 0
          })
        }
      }
      setBillingRunsWithProgress(runsWithProgress)
    } catch (_error) {
      log.error('Error fetching billing runs:', _error)
    } finally {
      setLoading(false)
    }
  }

  async function handleCreateBillingRun() {
    if (!token) return

    try {
      setCreating(true)
      const result = await createBillingRun(token)
      if (result) {
        setShowCreateDialog(false)
        fetchBillingRuns()
      }
    } catch (_error) {
      log.error('Error creating billing run:', _error)
    } finally {
      setCreating(false)
    }
  }

  async function handleDeleteClick(id: string) {
    if (!token) return

    const check = await getBillingRunDeleteCheck(token, id)
    setDeleteCheck(check)
    setDeleteTargetId(id)
  }

  async function handleConfirmDelete() {
    if (!token || !deleteTargetId) return

    try {
      setDeleting(true)
      await deleteBillingRun(token, deleteTargetId)
      setDeleteTargetId(null)
      setDeleteCheck(null)
      fetchBillingRuns()
    } catch (_error) {
      log.error('Error deleting billing run:', _error)
    } finally {
      setDeleting(false)
    }
  }

  const columns = createBillingRunColumns(handleDeleteClick)

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <Heading level={1}>{t('page.overview.title')}</Heading>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
{t('page.overview.description')}
          </p>
        </div>

        <Button
          onClick={() => setShowCreateDialog(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
{t('actions.createRun')}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="text-sm text-gray-500">{t('page.overview.loading')}</div>
        </div>
      ) : billingRunsWithProgress.length === 0 ? (
        <div className="flex flex-col justify-center items-center py-12">
          <div className="text-gray-500 mb-4">
            {t('page.overview.empty')}
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {t('page.overview.emptyAction')}
          </Button>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={billingRunsWithProgress}
          data-testid="billing-runs-table"
        />
      )}

      {/* Create Billing Run Dialog */}
      <Dialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)}>
        <DialogTitle>{t('dialog.create.title')}</DialogTitle>
        <DialogDescription>{t('dialog.create.description')}</DialogDescription>
        <DialogActions>
          <Button plain onClick={() => setShowCreateDialog(false)}>
            {t('actions.cancel')}
          </Button>
          <Button color="blue" onClick={handleCreateBillingRun} disabled={creating}>
            {creating ? t('actions.creating') : t('actions.createRun')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Billing Run Dialog */}
      <Dialog open={deleteTargetId !== null} onClose={() => setDeleteTargetId(null)}>
        <DialogTitle>{t('dialog.delete.title')}</DialogTitle>
        <DialogBody>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('dialog.delete.description')}
          </p>
          {deleteCheck && (deleteCheck.hasPaidItems || deleteCheck.hasSepaXml) && (
            <div className="mt-4 bg-amber-50 dark:bg-amber-950 border-l-4 border-amber-400 dark:border-amber-500 p-4">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                {t('dialog.delete.warningTitle')}
              </p>
              {deleteCheck.hasPaidItems && (
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  {t('dialog.delete.warningPaidItems')}
                </p>
              )}
              {deleteCheck.hasSepaXml && (
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  {t('dialog.delete.warningSepaXml')}
                </p>
              )}
            </div>
          )}
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setDeleteTargetId(null)}>
            {t('actions.cancel')}
          </Button>
          <Button color="red" onClick={handleConfirmDelete} disabled={deleting}>
            {t('actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default BillingOverview
